import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Clear as ClearIcon,
  Menu as MenuIcon,
  RemoveCircleOutlineOutlined,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControlLabel,
  Hidden,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  fetchError,
  getAllUsers,
  setAllUsers,
  showMessage,
  toggleNavCollapsed,
  updateUserLocations,
  updateUserOptInRoles,
} from "../../redux/actions";
import { fontColor } from "../../constants/ColorSets";
import {
  getOrgNameFromToken,
  isCurrentAccountAdmin,
  isCurrentAccountOrgManagerOrAdmin,
} from "../../components/AppBase/AppAuthProvider";
import AppInfoView from "../../components/AppBase/AppInfoView";
import AppConfirmDialog from "../../components/AppBase/AppConfirmDialog";
import AppLoader from "../../components/AppBase/AppLoader";
import UserAdministrationTable from "./UserAdministrationTable";
import { OrgUser } from "./OrgUser";
import MultiSelectFilter from "./MultiSelectFilter";
import { AppState } from "../../redux/store";
import { RoleState } from "../../types/models/RoleState";
import { getRoleBundles } from "../../redux/actions/Role";
import { TeamMembersState } from "../../types/models/TeamMembersState";
import {
  getLocationDisplayName,
  getRoleBundleDisplayName,
  getRoleDisplayName,
} from "./orgUserDisplay";

// Cap how many of these requests are ever in flight at once, so bulk actions
// against a large selection don't slam a rate-limited endpoint all at once.
const BULK_ACTION_BATCH_SIZE = 10;

const runInBatches = async <T,>(
  items: T[],
  batchSize: number,
  worker: (item: T) => Promise<boolean>
): Promise<boolean[]> => {
  const results: boolean[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(worker));
    results.push(...batchResults);
  }
  return results;
};

const UserAdministrationPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const roleState = useSelector<AppState, RoleState>((state) => state.role);
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);

  const [loading, setLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [selectedOrg, setSelectedOrg] = useState<string[]>([]);
  const [selectedRoleBundles, setSelectedRoleBundles] = useState<string[]>([]);
  const [selectedOptInRoles, setSelectedOptInRoles] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [openRemoveRolesConfirm, setOpenRemoveRolesConfirm] = useState<boolean>(false);
  const [openRemoveLocationsConfirm, setOpenRemoveLocationsConfirm] = useState<boolean>(false);
  const [showSelectedUsers, setShowSelectedUsers] = useState<boolean>(false);

  const organisation = getOrgNameFromToken();
  const isAllowed = isCurrentAccountOrgManagerOrAdmin();
  const isAdmin = isCurrentAccountAdmin();

  const userMatchesSearch = (user: OrgUser, term: string) => {
    const searchableText = [user.Name, user.Email].filter(Boolean).join(" ").toLowerCase();
    return searchableText.includes(term.toLowerCase());
  };

  const matchesAnySelected = (values: string[], selected: string[]) =>
    selected.length === 0 || selected.some((value) => values.includes(value));

  const countOccurrences = (values: string[]): Record<string, number> => {
    const counts: Record<string, number> = {};
    values.forEach((value) => {
      counts[value] = (counts[value] ?? 0) + 1;
    });
    return counts;
  };

  const roleBundleCounts = useMemo(
    () =>
      countOccurrences(
        (teamState.allUsers ?? []).flatMap((user) =>
          user.RoleBundleIds.map((id) => getRoleBundleDisplayName(id, roleState.roleBundles))
        )
      ),
    [teamState.allUsers, roleState.roleBundles]
  );

  const optInRoleCounts = useMemo(
    () =>
      countOccurrences(
        (teamState.allUsers ?? []).flatMap((user) =>
          user.OptInRoleIds.map((id) => getRoleDisplayName(id, roleState.knownRoles?.roles))
        )
      ),
    [teamState.allUsers, roleState.knownRoles]
  );

  const locationCounts = useMemo(
    () =>
      countOccurrences(
        (teamState.allUsers ?? []).flatMap((user) =>
          user.LniIds.map((id) => getLocationDisplayName(id, roleState.knownRoles?.locations))
        )
      ),
    [teamState.allUsers, roleState.knownRoles]
  );

  const orgCounts = useMemo(
    () => countOccurrences((teamState.allUsers ?? []).map((user) => user.Organisation)),
    [teamState.allUsers]
  );

  const displayUsers = useMemo(() => {
    return (teamState.allUsers ?? []).filter((user) => {
      if (appliedSearchTerm && !userMatchesSearch(user, appliedSearchTerm)) return false;
      if (
        !matchesAnySelected(
          user.OptInRoleIds.map((id) => getRoleDisplayName(id, roleState.knownRoles?.roles)),
          selectedOptInRoles
        )
      )
        return false;
      if (
        !matchesAnySelected(
          user.LniIds.map((id) => getLocationDisplayName(id, roleState.knownRoles?.locations)),
          selectedLocations
        )
      )
        return false;
      if (
        !matchesAnySelected(
          user.RoleBundleIds.map((id) => getRoleBundleDisplayName(id, roleState.roleBundles)),
          selectedRoleBundles
        )
      )
        return false;

      if (!matchesAnySelected([user.Organisation], selectedOrg)) return false;
      return true;
    });
  }, [
    teamState.allUsers,
    roleState.roleBundles,
    roleState.knownRoles,
    appliedSearchTerm,
    selectedOptInRoles,
    selectedLocations,
    selectedRoleBundles,
    selectedOrg,
  ]);

  const filteredUsers = useMemo(() => {
    if (!showSelectedUsers) return displayUsers;
    return (teamState.allUsers ?? []).filter((user) => selectedUsers.includes(user.Id));
  }, [displayUsers, selectedUsers, showSelectedUsers, teamState.allUsers]);

  const handleUpdateUser = (updatedUser: OrgUser) => {
    const updatedUsers = (teamState.allUsers ?? []).map((user) =>
      user.Id === updatedUser.Id ? updatedUser : user
    );
    dispatch(setAllUsers(updatedUsers));
  };

  const runBulkRemove = async (
    updater: (userId: string, callback: (success: boolean) => void) => void,
    fieldLabel: string
  ) => {
    const targetUserIds = [...selectedUsers];
    setLoading(true);
    const results = await runInBatches(targetUserIds, BULK_ACTION_BATCH_SIZE, (userId) => {
      return new Promise<boolean>((resolve) => {
        updater(userId, (success) => resolve(success));
      });
    });
    setLoading(false);

    const failedUserIds = targetUserIds.filter((_, index) => !results[index]);
    setSelectedUsers(failedUserIds);

    const succeededCount = results.filter(Boolean).length;
    const userWord = targetUserIds.length === 1 ? "user" : "users";

    if (failedUserIds.length === 0) {
      dispatch(
        showMessage(
          `Removed ${fieldLabel} for ${succeededCount} of ${targetUserIds.length} selected ${userWord}. Changes will be applied shortly.`
        )
      );
    } else if (succeededCount === 0) {
      dispatch(
        fetchError(
          `Failed to remove ${fieldLabel} for all ${targetUserIds.length} selected ${userWord}. Please try again.`
        )
      );
    } else {
      dispatch(
        fetchError(
          `Removed ${fieldLabel} for ${succeededCount} of ${targetUserIds.length} selected ${userWord}. ${failedUserIds.length} failed and remain selected — please retry.`
        )
      );
    }
  };

  const handleRemoveRoles = () => {
    setOpenRemoveRolesConfirm(false);
    runBulkRemove(
      (userId, callback) => dispatch(updateUserOptInRoles(userId, [], callback, true)),
      "opt-in roles"
    );
  };

  const handleRemoveLocations = () => {
    setOpenRemoveLocationsConfirm(false);
    runBulkRemove(
      (userId, callback) => dispatch(updateUserLocations(userId, [], callback, true)),
      "locations"
    );
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchText.trim());
  };

  const handleClearSearch = () => {
    setSearchText("");
    setAppliedSearchTerm("");
  };

  const toggleAllSelected = () => {
    setShowSelectedUsers(!showSelectedUsers);
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
    setShowSelectedUsers(false);
  };

  useEffect(() => {
    if (selectedUsers.length === 0 && showSelectedUsers) {
      setShowSelectedUsers(false);
    }
  }, [selectedUsers, showSelectedUsers]);

  useEffect(() => {
    // Validate permission
    if (!isAllowed) {
      navigate(-1);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    dispatch(
      getAllUsers(() => {
        dispatch(getRoleBundles(() => setLoading(false)));
      })
    );
  }, [dispatch]);

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AppLoader />
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: theme.page.bgColor,
          minHeight: "100vh",
        }}
      >
        <Box sx={{ width: "100%", pb: 0, pl: 2, pr: 2, pt: 2 }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                minHeight: "60px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Hidden lgUp>
                <IconButton
                  sx={{
                    color: "text.secondary",
                    marginLeft: 1,
                    padding: 0,
                  }}
                  edge="start"
                  className="menu-btn"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={() => dispatch(toggleNavCollapsed())}
                  size="large"
                >
                  <MenuIcon
                    sx={{
                      width: 35,
                      height: 35,
                    }}
                  />
                </IconButton>
              </Hidden>
              <Box sx={{ pt: 1, display: "flex", alignItems: "baseline", gap: 2 }}>
                <Typography
                  variant="h3"
                  component="h3"
                  color={fontColor.titleFontColor}
                  style={{ fontSize: 22, marginLeft: 12 }}
                >
                  User Administration
                </Typography>
                {!isAdmin && (
                  <Typography sx={{ fontSize: 14, color: theme.table.subTextColor }}>
                    <strong>Organisation:</strong>&nbsp;{organisation}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                mr: 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Box
                component="form"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSearch();
                }}
                sx={{ display: "flex", alignItems: "flex-end" }}
              >
                <TextField
                  id="search"
                  label="Search Users (name/email)"
                  variant="standard"
                  value={searchText}
                  sx={{ width: 240 }}
                  onChange={(event) => setSearchText(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {searchText && (
                          <IconButton
                            aria-label="clear search"
                            onClick={handleClearSearch}
                            edge="end"
                            size="small"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton aria-label="search" type="submit" edge="end" size="small">
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Button
                  variant="text"
                  startIcon={<RemoveCircleOutlineOutlined />}
                  disabled={selectedUsers.length === 0}
                  sx={{ textTransform: "none" }}
                  onClick={() => setOpenRemoveRolesConfirm(true)}
                >
                  Remove Roles
                </Button>
                <Button
                  variant="text"
                  startIcon={<RemoveCircleOutlineOutlined />}
                  disabled={selectedUsers.length === 0}
                  sx={{ textTransform: "none" }}
                  onClick={() => setOpenRemoveLocationsConfirm(true)}
                >
                  Remove Locations
                </Button>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showSelectedUsers}
                      disabled={selectedUsers.length === 0}
                      onChange={toggleAllSelected}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      style={{ color: selectedUsers.length === 0 ? "#b9b9b9" : "inherit" }}
                    >
                      Show selected
                    </Typography>
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderBottom: "1px solid lightgrey", mx: 1.5 }}></Box>

        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <MultiSelectFilter
                label="Role Bundles"
                options={roleState.roleBundles?.map((bundle) => bundle.DisplayName) || []}
                selected={selectedRoleBundles}
                onChange={setSelectedRoleBundles}
                counts={roleBundleCounts}
              />
              <MultiSelectFilter
                label="Opt-in Roles"
                options={roleState.knownRoles?.roles.map((role) => role.DisplayName) || []}
                selected={selectedOptInRoles}
                onChange={setSelectedOptInRoles}
                counts={optInRoleCounts}
              />
              <MultiSelectFilter
                label="Locations"
                options={roleState.knownRoles?.locations.map((location) => location.Name) || []}
                selected={selectedLocations}
                onChange={setSelectedLocations}
                counts={locationCounts}
              />
              {isAdmin && (
                <MultiSelectFilter
                  label="Organisation"
                  options={["KiwiRail", "Transdev Auckland", "Auckland One Rail"]}
                  selected={selectedOrg}
                  onChange={setSelectedOrg}
                  counts={orgCounts}
                />
              )}
              <Button
                variant="text"
                disabled={
                  selectedRoleBundles.length === 0 &&
                  selectedOptInRoles.length === 0 &&
                  selectedLocations.length === 0 &&
                  selectedOrg.length === 0
                }
                sx={{
                  textTransform: "none",
                  height: 40,
                  color:
                    selectedRoleBundles.length > 0 ||
                    selectedOptInRoles.length > 0 ||
                    selectedLocations.length > 0 ||
                    selectedOrg.length > 0
                      ? "primary.main"
                      : "text.secondary",
                }}
                onClick={() => {
                  setSelectedRoleBundles([]);
                  setSelectedOptInRoles([]);
                  setSelectedLocations([]);
                  setSelectedOrg([]);
                }}
              >
                Clear
              </Button>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
              <Button
                variant="outlined"
                disabled={selectedUsers.length === 0}
                sx={{ textTransform: "none", height: 40 }}
                onClick={handleClearSelection}
              >
                {selectedUsers.length > 0
                  ? `Clear Selection (${selectedUsers.length})`
                  : "Clear Selection"}
              </Button>
            </Box>
          </Box>

          <UserAdministrationTable
            users={filteredUsers}
            isAdmin={isAdmin}
            onUpdateUser={handleUpdateUser}
            selectedUsers={selectedUsers}
            onSelectedUsersChange={setSelectedUsers}
          />
        </Box>
        <Box sx={{ height: 100 }} />
      </Box>
      <AppConfirmDialog
        open={openRemoveRolesConfirm}
        onDeny={() => setOpenRemoveRolesConfirm(false)}
        onConfirm={handleRemoveRoles}
        dialogTitle="Remove roles?"
        title={`Remove all opt-in roles from ${selectedUsers.length} selected user${
          selectedUsers.length === 1 ? "" : "s"
        }?`}
      />
      <AppConfirmDialog
        open={openRemoveLocationsConfirm}
        onDeny={() => setOpenRemoveLocationsConfirm(false)}
        onConfirm={handleRemoveLocations}
        dialogTitle="Remove locations?"
        title={`Remove all locations from ${selectedUsers.length} selected user${
          selectedUsers.length === 1 ? "" : "s"
        }?`}
      />
      <AppInfoView />
    </>
  );
};

export default UserAdministrationPage;
